import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronDown, X } from 'lucide-react';
import { SequenceFormData, SequenceProp } from './types';
import { supabase } from '@/integrations/supabase/client';

export interface PropOption {
  prop_id: string;
  name: string;
  category_name: string;
  category_id: string;
}

interface PropsMultiSelectProps {
  form: UseFormReturn<SequenceFormData>;
  max?: number;
}

const PropsMultiSelect: React.FC<PropsMultiSelectProps> = ({
  form,
  max = 20
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<PropOption[]>([]);
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const selectedProps = (form.watch('props') || []) as SequenceProp[];

  // Load all active props on mount
  useEffect(() => {
    const load = async () => {
      const { data: props, error } = await supabase
        .from('props')
        .select(`
          id,
          name,
          category_id,
          prop_categories!inner(id, name)
        `)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error loading props:', error);
        return;
      }

      if (props) {
        const propOptions: PropOption[] = props.map((p: any) => ({
          prop_id: p.id,
          name: p.name,
          category_name: p.prop_categories.name,
          category_id: p.prop_categories.id
        }));
        setOptions(propOptions);
      }
    };
    load();
  }, []);

  // Search props when user types
  const searchProps = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) return;

    setIsSearching(true);
    try {
      const { data: props } = await supabase
        .from('props')
        .select(`
          id,
          name,
          category_id,
          prop_categories!inner(id, name)
        `)
        .eq('is_active', true)
        .ilike('name', `%${searchQuery}%`)
        .limit(20);

      if (props && props.length > 0) {
        setOptions(prevOptions => {
          const existingIds = new Set(prevOptions.map(o => o.prop_id));
          const newProps = props
            .filter((p: any) => !existingIds.has(p.id))
            .map((p: any) => ({
              prop_id: p.id,
              name: p.name,
              category_name: p.prop_categories.name,
              category_id: p.prop_categories.id
            }));

          return [...prevOptions, ...newProps];
        });
      }
    } catch (error) {
      console.error('Error searching props:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        searchProps(query.trim());
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchProps]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o =>
      o.name.toLowerCase().includes(q) ||
      o.category_name.toLowerCase().includes(q)
    );
  }, [options, query]);

  // Group props by category
  const groupedProps = useMemo(() => {
    const groups: Record<string, PropOption[]> = {};
    filtered.forEach(prop => {
      if (!groups[prop.category_name]) {
        groups[prop.category_name] = [];
      }
      groups[prop.category_name].push(prop);
    });
    return groups;
  }, [filtered]);

  const addProp = (propId: string) => {
    const current = form.getValues('props') || [];
    if (current.some(p => p.prop_id === propId)) return;
    if (current.length >= max) return;

    const next: SequenceProp[] = [...current, { prop_id: propId }];
    form.setValue('props', next, { shouldDirty: true, shouldValidate: true });
  };

  const removeProp = (propId: string) => {
    const current = form.getValues('props') || [];
    const next = current.filter(p => p.prop_id !== propId);
    form.setValue('props', next, { shouldDirty: true, shouldValidate: true });
  };

  // Get prop name by ID for display
  const getPropName = (propId: string): string => {
    const prop = options.find(o => o.prop_id === propId);
    return prop ? prop.name : propId;
  };

  // Get category badge color
  const getCategoryColor = (categoryName: string): string => {
    const colors: Record<string, string> = {
      'Mega Trees': 'bg-green-100 text-green-800',
      'Snowflakes': 'bg-blue-100 text-blue-800',
      'Arches': 'bg-purple-100 text-purple-800',
      'Matrix Panels': 'bg-red-100 text-red-800',
      'Wreaths': 'bg-yellow-100 text-yellow-800',
    };

    // Match partial category names (e.g., "Large Snowflakes" → Snowflakes color)
    for (const [key, value] of Object.entries(colors)) {
      if (categoryName.includes(key)) return value;
    }

    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {selectedProps.map(prop => {
          const propOption = options.find(o => o.prop_id === prop.prop_id);
          return (
            <Badge
              key={prop.prop_id}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {getPropName(prop.prop_id)}
              {propOption && (
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${getCategoryColor(propOption.category_name)}`}>
                  {propOption.category_name}
                </span>
              )}
              <button
                type="button"
                onClick={() => removeProp(prop.prop_id)}
                aria-label={`Remove ${getPropName(prop.prop_id)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-between w-full"
          >
            Add prop{selectedProps.length > 0 ? ` (${selectedProps.length}/${max})` : ''}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[400px]">
          <Command>
            <CommandInput
              placeholder="Search props (e.g., Mega Tree, Snowflake, Arch)..."
              value={query}
              onValueChange={setQuery}
            />
            <CommandEmpty>
              <div className="p-4 text-sm text-muted-foreground text-center">
                No props found. Try a different search term.
              </div>
            </CommandEmpty>
            <CommandList className="max-h-[300px]">
              {Object.entries(groupedProps).map(([categoryName, categoryProps]) => (
                <CommandGroup key={categoryName} heading={categoryName}>
                  {categoryProps.map(prop => {
                    const isSelected = selectedProps.some(p => p.prop_id === prop.prop_id);
                    return (
                      <CommandItem
                        key={prop.prop_id}
                        value={prop.name}
                        onSelect={() => {
                          if (!isSelected) {
                            addProp(prop.prop_id);
                          }
                          setQuery('');
                        }}
                        disabled={isSelected}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className={`flex-1 ${isSelected ? 'opacity-50' : ''}`}>
                            {prop.name}
                          </span>
                          {isSelected && (
                            <Badge variant="secondary" className="text-xs">
                              Added
                            </Badge>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
              {isSearching && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedProps.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Selected {selectedProps.length} of {max} props. These help buyers know what equipment is needed for your sequence.
        </p>
      )}
    </div>
  );
};

export default PropsMultiSelect;
